import React, { useEffect, useState } from 'react';
import {
	filter, merge, Subject, takeUntil, tap,
} from 'rxjs';
import useStyles from 'isomorphic-style-loader/useStyles';

import { Container } from '../../../../../symphony';

import { SlUIComponentsRenderer } from '../../../../../shared/public/SlLearnEngine/SlUIComponentsRenderer/SlUIComponentsRenderer';
import { SlDataService } from '../../../services/sl-data.service';
import {
	ActionType,
	IDisabledActions,
	IMaterialProps,
	RequestType,
} from '../../../global-interface';
import { SlLEContext } from '../../../global-constants';
import { FreezeLayer } from '../../FreezeLayer/FreezeLayer';
import { SlActions } from '../../../services/ActionsService/sl-actions';

import s from './Question.scss';
import { IAnswerOption, UIComponentType } from '../../../../../api/private/learn-engine-api/learn-engine-api.interface';

export function Question({
	materialInfo: { body, answer },
}: IMaterialProps): JSX.Element {
	useStyles(s);
	const [options, setOptions] = useState<IAnswerOption[]>(answer.options);
	const [disabledActions, setDisabledActions] = useState<IDisabledActions>(null);

	const { lessonFooterPrmBtn$, answerOptions$ } = Container.take(
		SlLEContext,
		SlDataService,
	);

	useEffect(() => {
		const unsubscriber = new Subject<void>();
		const { disabledActions$ } = Container.take(SlLEContext, SlDataService);

		merge(
			answerOptions$.pipe(
				filter((ao) => !!ao && !!ao.length),
				tap(setOptions),
			),
			disabledActions$.pipe(tap(setDisabledActions)),
		)
			.pipe(takeUntil(unsubscriber))
			.subscribe();

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	const setIsValid = (valid: boolean) => {
		lessonFooterPrmBtn$.next({
			...lessonFooterPrmBtn$.value,
			disabled: !valid,
		});
	};

	const updateOptions = (updatedOptions: IAnswerOption[]) => {
		answerOptions$.next(updatedOptions);
	};

	const compClickHandler = (type: UIComponentType) => {
		const { actions$ } = Container.take(SlLEContext, SlActions);

		switch (type) {
		case UIComponentType.CodeSnippet:
			return actions$.next({
				type: ActionType.OnCodeSnippetClick,
				requestType: RequestType.Parallel,
			});
		case UIComponentType.Image:
			return actions$.next({
				type: ActionType.OnImageClick,
				requestType: RequestType.Parallel,
				data: {
					imgUrl: body.find(
						(el) => el.componentTypeId === UIComponentType.Image,
					).content.data,
				},
			});
		default:
			return null;
		}
	};

	return (
		<div sl-test-data="cmpQuestion" className="le-question">
			<div sl-test-data="dvQuestionBody" className="le-question__body">
				<SlUIComponentsRenderer
					components={body}
					answerProps={{
						options,
						onChange: updateOptions,
						isValid: setIsValid,
					}}
					onClick={compClickHandler}
					disabled={disabledActions?.question}
				/>
			</div>
			{disabledActions?.question && <FreezeLayer />}
		</div>
	);
}
